import { useEffect, useRef, useState } from 'react';
import useAuth from './useAuth';
import authService from '../services/authService';

/**
 * useInactivityReturn — Dashboard State G (#569).
 *
 * Detects whether this is the first dashboard open after a longer absence and,
 * if so, lets the UI show a calm "welcome back" state. Runs its check exactly
 * once per session (after auth has resolved) and records the current visit.
 *
 * Two sources, picked by auth state:
 *  - authenticated → backend POST /account/seen, which returns the *previous*
 *    visit and stores now (cross-device, the preferred path).
 *  - anonymous → device-local localStorage fallback.
 *
 * "First ever visit" (no previous timestamp) is deliberately NOT a return.
 */

export const LAST_VISIT_KEY = 'gartenplaner_last_visit';
export const INACTIVITY_THRESHOLD_DAYS = 28;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function isInactiveSince(previousIso, thresholdDays) {
  if (!previousIso) return false; // never recorded → not a return
  const prev = new Date(previousIso);
  if (Number.isNaN(prev.getTime())) return false;
  return Date.now() - prev.getTime() > thresholdDays * MS_PER_DAY;
}

export default function useInactivityReturn(
  thresholdDays = INACTIVITY_THRESHOLD_DAYS
) {
  const { isAuthenticated, loading } = useAuth();
  const [isReturning, setIsReturning] = useState(false);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return; // run the check only once per session
    if (loading) return; // wait until auth has resolved
    ranRef.current = true;

    let cancelled = false;

    // Reading the previous visit is an external-system read (backend or
    // localStorage); resolving it in an async callback keeps the one-shot
    // setState out of the synchronous effect body.
    const resolveReturn = async () => {
      try {
        let previous;
        if (isAuthenticated) {
          const res = await authService.touchSeen();
          previous = res?.previous_last_seen_at;
        } else {
          previous = localStorage.getItem(LAST_VISIT_KEY);
          localStorage.setItem(LAST_VISIT_KEY, new Date().toISOString());
        }
        if (!cancelled) {
          setIsReturning(isInactiveSince(previous, thresholdDays));
        }
      } catch {
        // Non-fatal (request failed / localStorage unavailable) → no State G.
      }
    };

    resolveReturn();

    return () => {
      cancelled = true;
    };
  }, [loading, isAuthenticated, thresholdDays]);

  return isReturning;
}
